import React, { useState, useCallback } from "react";
import { Authenticator, Sidebar, Input, Button } from "components";
import { SidebarProvider } from "context/SidebarContext";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import {
  Campaigns,
  ErrorScreen,
  NotFoundScreen,
  LoginScreen,
  NewLoginScreen,
  LogoutScreen,
  CartScreen,
  CodCheckout,
  DashboardScreen,
  FaqScreen,
  PrivacyPolicy,
  ManualSetup,
  PlanScreen,
  StickyCartSettings,
  InvitationScreen,
  Affiliation,
  ReferralScreen,
  Integrations,
} from "screens";
import { withCampaignProvider } from "context/CampaignContext";
import { isEmbedded } from "lib/wrappers";
import styles from "styles/page.module.scss";
import useReactRouter from "use-react-router";
import classNames from "lib/classNames";
import { API } from "aws-amplify";
import TutorialModal from "components/TutorialModal";
import TallyModal from "components/TallyModal";
import SetupScreen from "screens/SetupScreen";

function App() {
  return (
    <BrowserRouter>
      <ErrorScreen>
        <Routes />
      </ErrorScreen>
    </BrowserRouter>
  );
}

export default App;

const Routes = () => {
  const [voucher, setVoucher] = useState("");
  const [newPlanRedirect, setNewPlanRedirect] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const { location } = useReactRouter();
  // const [collapsed, originalSetCollapsed] = useState(
  //   // JSON.parse(localStorage.getItem("sidebar_collapsed"))
  // );
  // const setCollapsed = (value) => {
  //   // localStorage.setItem("sidebar_collapsed", value);
  //   originalSetCollapsed(value);
  // };
  const [collapsed, setCollapsed] = useState(false);

  if (
    ["/login", "/auth/shopify/callback"].includes(location.pathname) ||
    new RegExp("/invitation/.*").test(location.pathname) ||
    new RegExp("/ref/.*").test(location.pathname)
  )
    return (
      <Switch>
        {/* <Route path="/login" component={LoginScreen} /> */}
        <Route path="/login" component={NewLoginScreen} />
        <Route path="/invitation/:id" component={InvitationScreen} />
        <Route path="/ref/:code" component={ReferralScreen} />
      </Switch>
    );
  else
    return (
      <Authenticator>
        <>
          <div
            className={classNames(
              "hidden px-20 pr-8 mx-auto py-2 flex-center bg-indigo-800 text-white font-bold"
            )}
          >
            <span className={classNames("flex-1")}>Have a voucher code?</span>
            <div className={classNames("w-2/3 flex items-center justify-end")}>
              <Input
                value={voucher}
                onChange={(value) => setVoucher(value)}
                isFlex
                placeholder="Voucher code"
              />
              <Button
                text="Use It Now"
                type="secondary2"
                className={classNames("ml-3")}
                onClick={async () => {
                  const planResponse = await API.post(
                    "API_GATEWAY_NEW",
                    "/shop/get_plan",
                    {
                      body: {
                        voucher,
                      },
                    }
                  );
                  setVoucher("");
                  setNewPlanRedirect(planResponse.confirmation_url);
                }}
              />
            </div>
          </div>
          <div className={styles.app_wrapper} data-collapsed={collapsed}>
            <SidebarProvider value={{ collapsed, setCollapsed }}>
              <Sidebar />
            </SidebarProvider>
            <Switch>
              <Route path="/logout" component={LogoutScreen} />
              <Route
                path="/"
                component={isEmbedded({
                  ChildComponent: SetupScreen,
                  previewOpenByDefault: false,
                  newPlanRedirect,
                  setIsOpen,
                })}
                exact={true}
              />
              <Route
                path="/campaigns"
                component={isEmbedded({
                  ChildComponent: Campaigns.list,
                  previewOpenByDefault: false,
                  newPlanRedirect,
                  setIsOpen,
                })}
                exact={true}
              />
              <Route
                path="/campaigns/create"
                component={isEmbedded({
                  ChildComponent: withCampaignProvider(
                    Campaigns.create,
                    null,
                    null,
                    setIsOpen
                  ),
                  newPlanRedirect,
                  setIsOpen,
                })}
                exact={true}
              />
              <Route
                path="/campaigns/create/:id"
                component={isEmbedded({
                  ChildComponent: Campaigns.duplicate,
                  newPlanRedirect,
                  setIsOpen,
                })}
              />
              <Route
                path="/campaigns/:id"
                component={isEmbedded({
                  ChildComponent: Campaigns.update,
                  newPlanRedirect,
                  setIsOpen,
                })}
              />
              <Route
                path="/settings"
                component={isEmbedded({
                  ChildComponent: CartScreen,
                  previewOpenByDefault: true,
                  noCampaign: true,
                  newPlanRedirect,
                })}
                exact={true}
              />

              <Route
                path="/sticky"
                component={isEmbedded({
                  ChildComponent: StickyCartSettings,
                  previewOpenByDefault: true,
                  noCampaign: true,
                  newPlanRedirect,
                })}
                exact={true}
              />
              <Route
                path="/settings/checkout"
                component={isEmbedded({
                  ChildComponent: CodCheckout,
                  previewOpenByDefault: true,
                  noCampaign: true,
                  newPlanRedirect,
                })}
                exact={true}
              />
              <Route
                path="/settings/setup"
                component={isEmbedded({
                  ChildComponent: ManualSetup,
                  previewOpenByDefault: false,
                  newPlanRedirect,
                })}
                exact={true}
              />
              <Route
                path="/dashboard"
                component={isEmbedded({
                  ChildComponent: DashboardScreen,
                  previewOpenByDefault: false,
                  newPlanRedirect,
                })}
                exact={true}
              />
              <Route
                path="/plan"
                component={isEmbedded({
                  ChildComponent: PlanScreen,
                  previewOpenByDefault: false,
                  newPlanRedirect,
                })}
              />
              <Route
                path="/integrations"
                component={isEmbedded({
                  ChildComponent: Integrations,
                  previewOpenByDefault: false,
                  newPlanRedirect,
                })}
              />
              <Route
                path="/faq"
                component={isEmbedded({
                  ChildComponent: FaqScreen,
                  previewOpenByDefault: false,
                  newPlanRedirect,
                })}
              />
              <Route
                path="/privacy_policy"
                component={isEmbedded({
                  ChildComponent: PrivacyPolicy,
                  previewOpenByDefault: false,
                  newPlanRedirect,
                })}
              />

              <Route
                path="/affiliation"
                component={isEmbedded({
                  ChildComponent: Affiliation,
                  previewOpenByDefault: false,
                  newPlanRedirect,
                })}
                exact={true}
              />
              <Route component={NotFoundScreen} />
            </Switch>
            <TutorialModal isOpen={isOpen} setIsOpen={setIsOpen} />
            <TallyModal />
          </div>
        </>
      </Authenticator>
    );
};
